import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ShipmentWaypoint } from '../models/waypoint/shipment.waypoint';
import { Location } from 'src/app/location/location.model';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FieldsetModule } from "primeng/fieldset";
import { IftaLabelModule } from "primeng/iftalabel";
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { CreateLocationComponent } from 'src/app/location/create-location/create-location.component';
import { LazyLocationPickerDirective } from 'src/app/shared/location-picker/lazy-location-picker.directive';
import { PermissionService } from 'src/app/security/permission.service';
import { app_permissions } from 'src/app/security/app-permissions';
import { SessionStorageService } from 'src/app/services/session-storage.service';

@Component({
    selector: 'app-waypoint-dialog',
    templateUrl: './waypoint-dialog.component.html',
    styleUrls: ['./waypoint-dialog.component.css'],
    standalone: true,
    imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    SelectModule,
    LazyLocationPickerDirective,
    ButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    FieldsetModule,
    IftaLabelModule,
    PageFormSectionComponent
],
    providers: [MessageService, DialogService]
})
export class WaypointDialogComponent implements OnInit, OnChanges {

  @ViewChild(LazyLocationPickerDirective) private locationPicker?: LazyLocationPickerDirective;

  @Input() addWaypointDialogVisible: boolean = false;
  @Input() newWaypoint: ShipmentWaypoint = new ShipmentWaypoint();
  @Input() companyLocations: Location[] = [];
  @Input() addWaypointInProgress: boolean = false;
  @Input() mode: 'create' | 'edit-location' = 'create';
  @Input() purpose: 'standard' | 'return' | 'delivery-only' = 'standard';

  @Output() onHide = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<ShipmentWaypoint>();

  submittedWaypoint: boolean = false;
  waypointTypeOptions: any[];

  constructor(
    private messageService: MessageService,
    private dialogService: DialogService,
    private permissionService: PermissionService,
    private sessionStorageService: SessionStorageService
  ) { }

  get canEditLocation(): boolean {
    return this.newWaypoint.location?.managedBy?.id === this.sessionStorageService.getCompany()?.id
      && this.permissionService.hasPermission(app_permissions.location.records.edit);
  }

  ngOnInit(): void {
    this.waypointTypeOptions = [
      { label: 'Pickup', value: 'PICKUP' },
      { label: 'Dropoff', value: 'DROPOFF' }
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['addWaypointDialogVisible'] && changes['addWaypointDialogVisible'].currentValue) {
      this.submittedWaypoint = false;
    }
  }

  get isLocationOnlyMode(): boolean {
    return this.mode === 'edit-location';
  }

  get isReturnMode(): boolean {
    return this.purpose === 'return';
  }

  get isDeliveryOnlyMode(): boolean {
    return this.purpose === 'delivery-only';
  }

  get dialogHeader(): string {
    if (this.isLocationOnlyMode) return 'Edit Waypoint Location';
    return this.isDeliveryOnlyMode ? 'Add Delivery-Only Stop' : this.isReturnMode ? 'Add Return Waypoint' : 'Add New Waypoint';
  }

  get sectionTitle(): string {
    if (this.isLocationOnlyMode) return 'Waypoint Location';
    return this.isDeliveryOnlyMode ? 'Operational Stop' : this.isReturnMode ? 'Return Destination' : 'Waypoint Details';
  }

  get sectionSubtitle(): string {
    if (this.isLocationOnlyMode) return 'Select a replacement location with latitude and longitude.';
    return this.isDeliveryOnlyMode
      ? 'This visit belongs only to this delivery. It does not change the shipment route or payload.'
      : this.isReturnMode
      ? 'Optionally select where the driver should return after the shipment is delivered.'
      : 'Select the location and stop type for this shipment route.';
  }

  get saveButtonLabel(): string {
    if (this.isLocationOnlyMode) return 'Save Location';
    return this.isDeliveryOnlyMode ? 'Add Operational Stop' : this.isReturnMode ? 'Save Return Waypoint' : 'Save Waypoint';
  }

  hideAddWaypointDialog() {
    this.onHide.emit();
  }

  hasValidCoordinates(location?: Location): boolean {
    const latitude = location?.location?.latitude;
    const longitude = location?.location?.longitude;
    return typeof latitude === 'number' && Number.isFinite(latitude) && latitude >= -90 && latitude <= 90
      && typeof longitude === 'number' && Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;
  }

  openLocationDialog(locationId?: number): void {
    const ref = this.dialogService.open(CreateLocationComponent, {
      header: locationId ? 'Edit Location & GPS Coordinates' : 'Create Location',
      modal: true,
      width: 'min(1100px, 96vw)',
      breakpoints: { '960px': '98vw' },
      contentStyle: { 'max-height': 'calc(100vh - 7rem)', overflow: 'auto' },
      data: { embedded: true, locationId },
      styleClass: 'shipment-location-dialog'
    });

    ref.onClose.subscribe((location: Location) => {
      if (!location) return;
      this.companyLocations = [...this.companyLocations.filter(existingLocation => existingLocation.id !== location.id), location];
      this.newWaypoint.location = location;
      this.locationPicker?.refreshSelectedLocation(location);
    });
  }

  saveNewWaypoint() {
    this.submittedWaypoint = true;
    if (this.isReturnMode && !this.newWaypoint.waypointType) {
      this.newWaypoint.waypointType = 'DROPOFF' as any;
    }
    if (this.isDeliveryOnlyMode) {
      this.newWaypoint.waypointType = 'INTERMEDIATE' as any;
    }
    if (!this.newWaypoint.location || (!this.isLocationOnlyMode && !this.newWaypoint.waypointType)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: this.isLocationOnlyMode ? 'Location is required.' : 'Location and Waypoint Type are required.'
        });
        return;
    }
    if (!this.hasValidCoordinates(this.newWaypoint.location)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Location needs coordinates',
        detail: 'Set valid latitude and longitude on the location, or choose another location.'
      });
      return;
    }
    this.onSave.emit(this.newWaypoint);
  }
}
